<template>
  <FullScreenLoader v-if="initialLoading" />

  <div class="create-event-page container-fluid p-0">
    <nav class="steps-navigation">
      <div class="progress-track">
        <div class="progress-fill" style="width: 100%;"></div>
      </div>

      <div class="steps-row">
        <div class="step-dot-item active">
          <div class="step-dot">1</div>
          <span class="step-dot-label">Dados bancários</span>
        </div>
      </div>
    </nav>

    <main class="step-content-wrapper">
      <div class="container-fluid">
        <div class="step-content py-4 p-0 pt-1 p-md-4">
          <div class="payment-stage-header mb-3">
            <div class="payment-stage-header__top">
              <h3 class="step-title mb-1">
                Dados Bancários
              </h3>
              <span class="payment-stage-security">
                <i class="fas fa-lock me-1"></i> Verificação segura
              </span>
            </div>
            <p class="step-description mb-2">
              Confira onde você receberá os valores dos seus mimos.
            </p>
            <div class="payment-stage-benefits payment-stage-benefits--compact">
              <span><i class="fas fa-check-circle me-1"></i> Transparência</span>
              <span><i class="fas fa-check-circle me-1"></i> Verificação segura</span>
              <span><i class="fas fa-check-circle me-1"></i> Dados protegidos</span>
            </div>
          </div>

          <!-- Tabs -->
          <div class="tab-nav mb-3">
            <button
              class="tab-btn"
              :class="{ active: activeTab === 'ted' }"
              :disabled="isEditingPix"
              @click="activeTab = 'ted'"
            >
              <i class="fas fa-building-columns me-1"></i> Dados para TED
            </button>
            <button
              class="tab-btn"
              :class="{ active: activeTab === 'pix' }"
              :disabled="isEditingPix && activeTab !== 'pix'"
              @click="activeTab = 'pix'"
            >
              <i class="fas fa-qrcode me-1"></i> Dados para Pix
            </button>
          </div>

          <!-- TED Tab -->
          <div v-if="activeTab === 'ted'">
            <section v-if="isEmbeddedVisible" class="bank-summary-section bank-summary-section--embedded">
              <div class="payment-embed-shell">
                <div v-if="showEmbeddedSkeleton" class="embedded-skeleton" aria-hidden="true">
                  <div class="embedded-skeleton__line embedded-skeleton__line--title"></div>
                  <div class="embedded-skeleton__line"></div>
                  <div class="embedded-skeleton__line embedded-skeleton__line--short"></div>
                  <div class="embedded-skeleton__card"></div>
                  <div class="embedded-skeleton__card embedded-skeleton__card--small"></div>
                </div>
                <div
                  ref="embeddedOnboardingHost"
                  class="embedded-onboarding-host"
                  :class="{ 'embedded-onboarding-host--ready': embeddedContentReady }"
                ></div>
              </div>
            </section>

            <section v-else class="bank-summary-section">
              <div class="bank-summary-section__header mb-2">
                <h5 class="mb-1">Dados de Recebimento</h5>
                <p class="text-muted small mb-0">Confira onde você receberá os valores dos seus mimos.</p>
              </div>
              <div class="bank-status-chip mb-3" :class="bankStatusClass">
                <i :class="bankStatusIcon"></i>
                <span>{{ bankStatusLabel }}</span>
              </div>
              <div v-if="bankDetails.has_bank" class="bank-summary-section__content">
                <p class="bank-summary-line mb-2">
                  {{ displayBankName }}
                  <span class="dot-separator">•</span>
                  Ag: {{ bankDetails.agency || '---' }}
                  <span class="dot-separator">•</span>
                  Conta: {{ bankDetails.account_masked || '******' }}
                </p>
                <p class="bank-summary-meta mb-0">Titular deve ser o mesmo do CPF/CNPJ cadastrado.</p>
              </div>
              <div v-else class="bank-summary-section__empty">
                <i class="fas fa-building-columns me-2"></i>
                Nenhum dado bancário ativo encontrado no momento.
              </div>
            </section>
          </div>

          <!-- Pix Tab -->
          <div v-else>
            <!-- Display mode -->
            <section v-if="!isEditingPix" class="bank-summary-section">
              <div class="bank-summary-section__header mb-2">
                <h5 class="mb-1">Chave Pix</h5>
                <p class="text-muted small mb-0">Sua chave Pix para recebimento de transferências.</p>
              </div>

              <div v-if="pixDetails">
                <div class="bank-status-chip bank-status-chip--ok mb-3">
                  <i class="fas fa-circle-check"></i>
                  <span>Chave Pix cadastrada</span>
                </div>
                <p class="fw-semibold mb-1">{{ pixDetails.holder || pixDetails.full_name || '—' }}</p>
                <p class="text-muted mb-1" style="font-size: 0.88rem;">
                  <strong>{{ pixKeyTypeLabel(pixDetails.agency || pixDetails.key_type) }}</strong>: {{ maskedPixDetailsKey }}
                </p>
              </div>
              <div v-else class="bank-summary-section__empty">
                <i class="fas fa-qrcode me-2"></i>
                Nenhuma chave Pix cadastrada.
              </div>
            </section>

            <!-- Edit mode – step 0: form -->
            <section v-else-if="pixCodeStep === 0" class="bank-summary-section">
              <div class="bank-summary-section__header mb-3">
                <h5 class="mb-1">{{ pixDetails ? 'Editar' : 'Cadastrar' }} chave Pix</h5>
                <p class="text-muted small mb-0">Preencha os dados e confirme por e-mail.</p>
              </div>

              <div class="mb-3">
                <label class="form-label fw-semibold" style="font-size: 0.88rem;">Tipo de chave</label>
                <select v-model="pixForm.key_type" class="form-select form-select-sm">
                  <option value="">Selecione o tipo</option>
                  <option value="CPF">CPF</option>
                  <option value="CNPJ">CNPJ</option>
                  <option value="EMAIL">E-mail</option>
                  <option value="PHONE">Telefone</option>
                  <option value="EVP">Chave aleatória (EVP)</option>
                </select>
              </div>

              <div class="mb-3">
                <label class="form-label fw-semibold" style="font-size: 0.88rem;">Chave Pix</label>
                <input
                  :value="pixForm.pix_key"
                  @input="onPixKeyInput"
                  type="text"
                  :inputmode="pixForm.key_type === 'EMAIL' || pixForm.key_type === 'EVP' ? 'text' : 'numeric'"
                  class="form-control form-control-sm"
                  :placeholder="pixKeyPlaceholder"
                />
              </div>

              <div class="mb-3">
                <label class="form-label fw-semibold" style="font-size: 0.88rem;">Nome do titular</label>
                <input
                  v-model="pixForm.full_name"
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Nome completo do titular"
                />
              </div>
            </section>

            <!-- Edit mode – step 1: code confirmation -->
            <section v-else class="bank-summary-section">
              <div class="bank-summary-section__header mb-3">
                <h5 class="mb-1">Confirme por e-mail</h5>
                <p class="text-muted small mb-2">
                  Enviamos um código de 6 dígitos para <strong>{{ pixMaskedEmail }}</strong>. Insira abaixo para confirmar.
                </p>
              </div>

              <div class="mb-3">
                <label class="form-label fw-semibold" style="font-size: 0.88rem;">Código de verificação</label>
                <input
                  v-model="pixCode"
                  type="text"
                  class="form-control form-control-sm"
                  :class="{ 'is-invalid': pixCodeError }"
                  maxlength="6"
                  placeholder="000000"
                />
                <div v-if="pixCodeError" class="invalid-feedback">{{ pixCodeError }}</div>
              </div>

              <div class="text-center mt-3">
                <button
                  v-if="pixResendCountdown === 0"
                  class="btn btn-link btn-sm p-0"
                  @click="requestPixCode"
                  :disabled="isRequestingPixCode"
                >
                  Reenviar código
                </button>
                <span v-else class="text-muted small">Reenviar em {{ pixResendCountdown }}s</span>
              </div>
            </section>
          </div>

        </div>
      </div>
    </main>

    <footer class="app-footer">
      <div class="app-footer__inner">
        <p class="app-footer__step-hint">
          Passo 1 de 1 — <strong>Dados bancários</strong>
        </p>
        <div class="app-footer__actions">
          <!-- back / cancel -->
          <button
            v-if="activeTab !== 'pix' || !isEditingPix"
            class="btn btn-footer-back"
            @click="goBackToDashboard"
            :disabled="stripeActionLoading"
          >
            <i class="fas fa-chevron-left me-1"></i> Voltar
          </button>
          <button
            v-else
            class="btn btn-footer-back"
            @click="cancelPixEdit"
          >
            <i class="fas fa-chevron-left me-1"></i> Cancelar edição
          </button>

          <!-- TED action -->
          <button
            v-if="activeTab === 'ted'"
            class="btn btn-footer-finish"
            @click="handlePrimaryAction"
            :disabled="isPrimaryActionDisabled"
          >
            <span v-if="stripeActionLoading" class="spinner-border spinner-border-sm me-2"></span>
            {{ primaryActionLabel }}
          </button>

          <!-- Pix actions -->
          <template v-else-if="activeTab === 'pix'">
            <button
              v-if="!isEditingPix"
              class="btn btn-footer-finish"
              @click="startPixEdit"
            >
              {{ pixDetails ? 'Editar chave Pix' : 'Cadastrar chave Pix' }}
            </button>
            <button
              v-else-if="pixCodeStep === 0"
              class="btn btn-footer-finish"
              :disabled="isRequestingPixCode || !pixForm.key_type || !pixForm.pix_key || !pixForm.full_name"
              @click="requestPixCode"
            >
              <span v-if="isRequestingPixCode" class="spinner-border spinner-border-sm me-1" />
              {{ isRequestingPixCode ? 'Enviando...' : 'Enviar código' }}
            </button>
            <button
              v-else
              class="btn btn-footer-finish"
              :disabled="isSavingPix || pixCode.length !== 6"
              @click="confirmPixUpdate"
            >
              <span v-if="isSavingPix" class="spinner-border spinner-border-sm me-1" />
              {{ isSavingPix ? 'Confirmando...' : 'Confirmar' }}
            </button>
          </template>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { toast } from 'vue3-toastify';
import { useRouter, useRoute } from 'vue-router';
import { loadConnectAndInitialize } from '@stripe/connect-js/pure';
import { useAuthStore } from '@/stores/authStore';
import FullScreenLoader from '@/components/FullScreenLoader.vue';
import { maskPixKey } from '@/utils/pixHelpers';
import api from '@/services/api';

const authStore = useAuthStore();
const router = useRouter();
const route = useRoute();
const stripePublishableKey = (process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY || '').trim();

const initialLoading = ref(true);
const stripeActionLoading = ref(false);
const isEmbeddedVisible = ref(false);
const embeddedInitializing = ref(false);
const embeddedContentReady = ref(false);
const embeddedOnboardingHost = ref(null);
const onboardingElement = ref(null);

// Tabs state — lê ?tab= da query para focar na aba certa
const activeTab = ref(route.query.tab === 'pix' ? 'pix' : 'ted');
// Pix tab state
const isEditingPix = ref(false);
const pixDetails = ref(null);
const pixForm = ref({ key_type: '', pix_key: '', full_name: '' });
const pixCodeStep = ref(0); // 0: form, 1: code entry
const pixCode = ref('');
const pixCodeError = ref('');
const isSavingPix = ref(false);
const isRequestingPixCode = ref(false);
const pixMaskedEmail = ref('');
const pixResendCountdown = ref(0);
let resendTimer = null;

const bankDetails = ref({
  has_bank: false,
  bank_name: '',
  agency: '',
  account_masked: '',
  account_holder: '',
});

const bankStatusClass = computed(() => (bankDetails.value.has_bank ? 'bank-status-chip--ok' : 'bank-status-chip--warn'));

const bankStatusIcon = computed(() => (bankDetails.value.has_bank ? 'fas fa-circle-check' : 'fas fa-triangle-exclamation'));

const bankStatusLabel = computed(() => {
  if (bankDetails.value.has_bank) {
    return 'Conta ativa e verificada';
  }

  return 'Conta pendente de verificacao';
});

const displayBankName = computed(() => {
  const rawName = String(bankDetails.value.bank_name || '').trim();

  if (rawName) {
    return rawName;
  }

  if (bankDetails.value.has_bank) {
    return 'Banco cadastrado';
  }

  return 'Banco nao identificado';
});

const maskedPixDetailsKey = computed(() => {
  const keyType = pixDetails.value?.agency || pixDetails.value?.key_type;
  const masked = maskPixKey(pixDetails.value?.account || pixDetails.value?.pix_key, keyType);
  return masked || '—';
});

const isPrimaryActionDisabled = computed(() => stripeActionLoading.value);

const showEmbeddedSkeleton = computed(() => {
  if (!isEmbeddedVisible.value) {
    return false;
  }

  if (stripeActionLoading.value || embeddedInitializing.value) {
    return true;
  }

  if (!onboardingElement.value) {
    return false;
  }

  return !embeddedContentReady.value;
});

const primaryActionLabel = computed(() => {
  if (stripeActionLoading.value) {
    return 'Preparando...';
  }

  if (isEmbeddedVisible.value) {
    return 'Cancelar edicao';
  }

  return 'Atualizar dados bancarios';
});

function handlePrimaryAction() {
  if (isEmbeddedVisible.value) {
    closeEmbeddedBankUpdate();
    return;
  }

  handleUpdateBankDetails();
}

onMounted(async () => {
  try {
    await Promise.all([fetchActiveBankDetails(), fetchPixDetails()]);
    // Se ?tab=pix, abrir modo edição direto quando não há chave cadastrada
    if (route.query.tab === 'pix' && !pixDetails.value) {
      startPixEdit();
    }
  } finally {
    initialLoading.value = false;
  }
});

async function fetchActiveBankDetails() {
  try {
    const response = await api.get('/users/finance/connected-accounts/stripe/active-bank-details');
    const payload = response?.data || {};

    bankDetails.value = {
      has_bank: Boolean(payload?.has_bank ?? payload?.hasBank),
      bank_name: String(payload?.bank_name ?? payload?.bankName ?? ''),
      agency: String(payload?.agency || ''),
      account_masked: String(payload?.account_masked ?? payload?.accountMasked ?? ''),
      account_holder: String(payload?.account_holder ?? payload?.accountHolder ?? ''),
    };
  } catch {
    bankDetails.value = {
      has_bank: false,
      bank_name: '',
      agency: '',
      account_masked: '',
      account_holder: '',
    };
  }
}

async function handleUpdateBankDetails() {
  if (isEmbeddedVisible.value || stripeActionLoading.value) {
    return;
  }

  stripeActionLoading.value = true;

  try {
    const response = await api.post('/users/finance/connected-accounts/stripe/bank-update-session');
    const clientSecret = String(response?.data?.account_session_client_secret || '').trim();

    if (!clientSecret) {
      throw new Error('Sessao embutida indisponivel no momento.');
    }

    await mountEmbeddedBankUpdate(clientSecret);
  } catch (err) {
    const message = err?.response?.data?.message || err?.message || 'Nao foi possivel iniciar a atualizacao agora.';
    toast.error(message, { autoClose: 3500, position: 'top-right' });
  } finally {
    stripeActionLoading.value = false;
  }
}

function clearEmbeddedOnboarding() {
  if (!embeddedOnboardingHost.value || !onboardingElement.value) {
    embeddedContentReady.value = false;
    return;
  }

  if (embeddedOnboardingHost.value.contains(onboardingElement.value)) {
    embeddedOnboardingHost.value.removeChild(onboardingElement.value);
  }

  onboardingElement.value = null;
  embeddedContentReady.value = false;
}

const createStripeEmbeddedSurfaceTracker = () => {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') {
    return {
      waitUntilSettled: async () => {},
      restore: () => {},
    };
  }

  const originalFetch = window.fetch.bind(window);
  let requestSeen = false;
  let pendingCount = 0;
  let settledResolver = null;
  let fallbackTimer = null;

  const settleIfReady = () => {
    if (!requestSeen || pendingCount > 0 || !settledResolver) {
      return;
    }

    const resolve = settledResolver;
    settledResolver = null;
    resolve();
  };

  window.fetch = async (...args) => {
    const input = args[0];
    const requestURL = typeof input === 'string'
      ? input
      : String(input?.url || '');

    const isEmbeddedSurfaceRequest = requestURL.includes('/v1/embedded_surfaces');

    if (isEmbeddedSurfaceRequest) {
      requestSeen = true;
      pendingCount += 1;
    }

    try {
      return await originalFetch(...args);
    } finally {
      if (isEmbeddedSurfaceRequest) {
        pendingCount = Math.max(0, pendingCount - 1);
        settleIfReady();
      }
    }
  };

  return {
    waitUntilSettled: () => new Promise((resolve) => {
      if (requestSeen && pendingCount === 0) {
        resolve();
        return;
      }

      settledResolver = resolve;
      fallbackTimer = setTimeout(() => {
        if (settledResolver) {
          const fallbackResolve = settledResolver;
          settledResolver = null;
          fallbackResolve();
        }
      }, 2200);
    }),
    restore: () => {
      window.fetch = originalFetch;
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
      }
      settledResolver = null;
      fallbackTimer = null;
    },
  };
};

const waitForEmbeddedRender = async () => {
  const host = embeddedOnboardingHost.value;
  if (!host) return;

  const isRenderableStripeIframe = (iframe) => {
    if (!iframe) return false;

    const id = String(iframe.id || '').trim();
    const name = String(iframe.name || '').trim();
    if (id === 'stripeDataLayerFrame' || name.startsWith('connectjs_controller')) {
      return false;
    }

    const style = window.getComputedStyle(iframe);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) {
      return false;
    }

    const rect = iframe.getBoundingClientRect();
    return rect.width >= 40 && rect.height >= 120;
  };

  const findRenderableIframe = () => {
    const candidates = host.querySelectorAll('iframe');
    for (const iframe of candidates) {
      if (isRenderableStripeIframe(iframe)) {
        return iframe;
      }
    }
    return null;
  };

  const waitForIframeLoad = (iframe) => new Promise((resolve) => {
    if (!iframe) {
      resolve();
      return;
    }

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      iframe.removeEventListener('load', onLoad);
      clearTimeout(timeoutId);
      resolve();
    };

    const onLoad = () => finish();
    iframe.addEventListener('load', onLoad, { once: true });

    const timeoutId = setTimeout(() => {
      finish();
    }, 12000);
  });

  const existingRenderable = findRenderableIframe();
  if (existingRenderable) {
    await waitForIframeLoad(existingRenderable);
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    return;
  }

  await new Promise((resolve) => {
    let resolved = false;

    const finish = () => {
      if (resolved) return;
      resolved = true;
      observer.disconnect();
      clearTimeout(timeoutId);
      resolve();
    };

    const observer = new MutationObserver(async () => {
      const iframe = findRenderableIframe();
      if (iframe) {
        observer.disconnect();
        await waitForIframeLoad(iframe);
        await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
        finish();
      }
    });

    observer.observe(host, { childList: true, subtree: true });

    const timeoutId = setTimeout(() => {
      finish();
    }, 15000);
  });
};

async function mountEmbeddedBankUpdate(clientSecret) {
  if (!stripePublishableKey) {
    throw new Error('VUE_APP_STRIPE_PUBLISHABLE_KEY nao configurada.');
  }

  embeddedInitializing.value = true;
  embeddedContentReady.value = false;
  isEmbeddedVisible.value = true;
  await nextTick();

  if (!embeddedOnboardingHost.value) {
    throw new Error('Container de onboarding nao encontrado.');
  }

  clearEmbeddedOnboarding();
  const stripeSurfaceTracker = createStripeEmbeddedSurfaceTracker();
  const skeletonStartedAt = Date.now();

  const connectAppearance = {
    variables: {
      colorPrimary: '#FF4D94',
      colorBackground: '#ffffff',
      colorText: '#32355d',
      colorSecondaryText: '#6c757d',
      colorBorder: '#e8ebf2',
      actionPrimaryColorText: '#FF4D94',
      buttonPrimaryColorBackground: '#FF4D94',
      buttonPrimaryColorBorder: '#FF4D94',
      buttonPrimaryColorText: '#ffffff',
      borderRadius: '12px',
      formBorderRadius: '12px',
      buttonBorderRadius: '12px',
      overlayBorderRadius: '14px',
      fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, sans-serif',
    },
  };

  const connectInstance = loadConnectAndInitialize({
    publishableKey: stripePublishableKey,
    fetchClientSecret: async () => clientSecret,
    appearance: connectAppearance,
    fonts: [
      {
        cssSrc: 'https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap',
      },
    ],
  });

  const accountOnboarding = connectInstance.create('account-onboarding');
  accountOnboarding.setCollectionOptions({
    fields: 'currently_due',
    futureRequirements: 'include',
  });

  accountOnboarding.setOnLoadError(() => {
    embeddedContentReady.value = false;
    embeddedInitializing.value = false;
  });

  accountOnboarding.setOnExit(async () => {
    isEmbeddedVisible.value = false;
    embeddedInitializing.value = false;
    clearEmbeddedOnboarding();
    await fetchActiveBankDetails();
  });

  try {
    embeddedOnboardingHost.value.appendChild(accountOnboarding);
    onboardingElement.value = accountOnboarding;

    await waitForEmbeddedRender();
    await stripeSurfaceTracker.waitUntilSettled();

    const minSkeletonMs = 1300;
    const elapsed = Date.now() - skeletonStartedAt;
    if (elapsed < minSkeletonMs) {
      await new Promise((resolve) => setTimeout(resolve, minSkeletonMs - elapsed));
    }

    await new Promise((resolve) => setTimeout(resolve, 160));
    embeddedContentReady.value = true;
    embeddedInitializing.value = false;
  } finally {
    stripeSurfaceTracker.restore();
  }
}

function closeEmbeddedBankUpdate() {
  isEmbeddedVisible.value = false;
  clearEmbeddedOnboarding();
}

onBeforeUnmount(() => {
  clearEmbeddedOnboarding();
  clearResendTimer();
});

function goBackToDashboard() {
  router.push('/my-account/dashboard');
}

function pixKeyTypeLabel(keyType) {
  const map = { CPF: 'CPF', CNPJ: 'CNPJ', EMAIL: 'E-mail', PHONE: 'Telefone', EVP: 'Chave aleatória' };
  return map[keyType] || keyType || 'Tipo';
}

async function fetchPixDetails() {
  try {
    const res = await api.get('/users/account-receiver');
    const pix = res?.data;
    pixDetails.value = pix?.account ? pix : null;
  } catch {
    pixDetails.value = null;
  }
}

function applyPixMask(value, keyType) {
  const digits = value.replace(/\D/g, '');
  if (keyType === 'CPF') {
    return digits.slice(0, 11)
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  if (keyType === 'CNPJ') {
    return digits.slice(0, 14)
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  }
  if (keyType === 'PHONE') {
    return digits.slice(0, 11)
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d{1,4})$/, '$1-$2');
  }
  return value;
}

function onPixKeyInput(event) {
  const raw = event.target.value;
  const masked = applyPixMask(raw, pixForm.value.key_type);
  pixForm.value.pix_key = masked;
}

const pixKeyPlaceholder = computed(() => {
  const map = {
    CPF: '000.000.000-00',
    CNPJ: '00.000.000/0000-00',
    EMAIL: 'seuemail@exemplo.com',
    PHONE: '(00) 00000-0000',
    EVP: 'Chave aleatória (UUID)',
  };
  return map[pixForm.value.key_type] || 'Digite sua chave Pix';
});

watch(() => pixForm.value.key_type, () => {
  pixForm.value.pix_key = '';
});

function startPixEdit() {
  const userName = authStore.user?.name || authStore.user?.fullName || '';
  if (pixDetails.value) {
    pixForm.value = {
      key_type: pixDetails.value.agency || pixDetails.value.key_type || '',
      pix_key: pixDetails.value.account || pixDetails.value.pix_key || '',
      full_name: pixDetails.value.holder || pixDetails.value.full_name || userName,
    };
  } else {
    pixForm.value = { key_type: '', pix_key: '', full_name: userName };
  }
  pixCodeStep.value = 0;
  pixCode.value = '';
  pixCodeError.value = '';
  isEditingPix.value = true;
}

function cancelPixEdit() {
  isEditingPix.value = false;
  pixCodeStep.value = 0;
  pixCode.value = '';
  pixCodeError.value = '';
  clearResendTimer();
}

function startResendCountdown(seconds = 60) {
  pixResendCountdown.value = seconds;
  clearResendTimer();
  resendTimer = setInterval(() => {
    pixResendCountdown.value -= 1;
    if (pixResendCountdown.value <= 0) {
      clearResendTimer();
    }
  }, 1000);
}

function clearResendTimer() {
  if (resendTimer) {
    clearInterval(resendTimer);
    resendTimer = null;
  }
  pixResendCountdown.value = 0;
}

async function requestPixCode() {
  if (isRequestingPixCode.value) return;
  isRequestingPixCode.value = true;
  pixCodeError.value = '';
  try {
    const res = await api.post('/users/finance/pix/request-code');
    pixMaskedEmail.value = res?.data?.masked_email || '';
    pixCodeStep.value = 1;
    pixCode.value = '';
    startResendCountdown(60);
    toast.success('Código enviado por e-mail!', { autoClose: 3000, position: 'top-right' });
  } catch (err) {
    const msg = err?.response?.data?.message || 'Não foi possível enviar o código.';
    toast.error(msg, { autoClose: 3500, position: 'top-right' });
  } finally {
    isRequestingPixCode.value = false;
  }
}

async function confirmPixUpdate() {
  if (isSavingPix.value) return;
  isSavingPix.value = true;
  pixCodeError.value = '';
  try {
    await api.put('/users/finance/pix', {
      code: pixCode.value,
      key_type: pixForm.value.key_type,
      pix_key: pixForm.value.pix_key,
      full_name: pixForm.value.full_name,
    });
    toast.success('Chave Pix atualizada com sucesso!', { autoClose: 3000, position: 'top-right' });
    await fetchPixDetails();
    cancelPixEdit();
  } catch (err) {
    const msg = err?.response?.data?.message || 'Código inválido ou expirado.';
    pixCodeError.value = msg;
  } finally {
    isSavingPix.value = false;
  }
}</script>

<style scoped>
.create-event-page {
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
  min-height: 100%;
  width: 100%;
  min-width: 600px;
  overflow: visible;
}

.steps-navigation {
  position: -webkit-sticky;
  position: sticky;
  top: 0;
  z-index: 90;
  background-color: #fff;
  border-bottom: 1px solid #f0f0f0;
  padding: 1rem 1.5rem 0.75rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  margin-top: 0;
}

.progress-track {
  position: relative;
  height: 3px;
  background-color: #e9ecef;
  border-radius: 99px;
  margin: 0 auto 1rem;
  max-width: 320px;
}

.progress-fill {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: linear-gradient(90deg, #ff50a3, #ff8cc8);
  border-radius: 99px;
}

.steps-row {
  display: flex;
  justify-content: center;
  max-width: 360px;
  margin: 0 auto;
  gap: 8px;
}

.step-dot-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  opacity: 1;
  min-width: 0;
  flex: 1;
}

.step-dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: #ff50a3;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: #fff;
  box-shadow: 0 0 0 4px rgba(255, 80, 163, 0.18);
}

.step-dot-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: #ff50a3;
  white-space: nowrap;
}

.step-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #32355d;
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
}

.step-description {
  font-size: 0.92rem;
  color: #6c757d;
  margin-bottom: 0.55rem;
}

.payment-stage-header {
  background: #ffffff;
  border: 1px solid #e9edf5;
  border-radius: 0.9rem;
  padding: 0.9rem 1rem 0.75rem;
}

.payment-stage-header__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.7rem;
  margin-bottom: 0.2rem;
}

.title-verified-seal {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.62rem;
  color: #ffffff;
  background: #2f80ed;
  box-shadow: 0 0 0 2px rgba(47, 128, 237, 0.16);
}

.payment-stage-security {
  display: inline-flex;
  align-items: center;
  font-size: 0.76rem;
  font-weight: 600;
  color: #6b7285;
  background: #f7f9fc;
  border: 1px solid #e6ebf4;
  border-radius: 999px;
  padding: 0.2rem 0.5rem;
  margin-left: auto;
  white-space: nowrap;
}

.payment-stage-benefits {
  display: flex;
  gap: 0.45rem;
  font-size: 0.74rem;
  color: #5a647f;
}

.payment-stage-benefits--compact {
  flex-wrap: nowrap;
  overflow-x: auto;
  scrollbar-width: none;
}

.payment-stage-benefits--compact::-webkit-scrollbar {
  display: none;
}

.payment-stage-benefits span {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  background: #f8faff;
  border: 1px solid #e8edf6;
  border-radius: 999px;
  padding: 0.16rem 0.48rem;
}

.bank-summary-section {
  border: 1px dashed #d8deea;
  border-radius: 14px;
  background: #ffffff;
  padding: 1rem;
}

.bank-summary-section--embedded {
  border: none;
  background: transparent;
  padding: 0;
  margin-top: 0.7rem;
  margin-bottom: 0.35rem;
}

.payment-embed-shell {
  border: none;
  border-radius: 0;
  background: transparent;
  padding: 0;
  box-shadow: none;
}

.bank-summary-section__header h5 {
  font-size: 1rem;
  color: #2f3456;
  font-weight: 700;
}

.bank-status-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  border-radius: 999px;
  padding: 0.33rem 0.7rem;
  font-size: 0.82rem;
  font-weight: 700;
}

.bank-status-chip--ok {
  background: #ecf8f2;
  border: 1px solid #c7ead6;
  color: #1f7f54;
}

.bank-status-chip--warn {
  background: #fff7eb;
  border: 1px solid #ffe1b8;
  color: #a86706;
}

.bank-summary-line {
  color: #384262;
  font-weight: 700;
  font-size: 0.94rem;
}

.bank-summary-meta {
  font-size: 0.86rem;
  color: #6b738c;
}

.bank-summary-section__empty {
  padding: 0.8rem;
  border-radius: 10px;
  background: #f9fbff;
  color: #66708b;
  font-size: 0.88rem;
}

.dot-separator {
  margin: 0 0.35rem;
  color: #96a0bb;
}

.embedded-onboarding-host {
  width: 100%;
  min-height: 5px;
  background: #ffffff;
  border: 1px solid #f0f2f7;
  border-radius: 14px;
  box-shadow: 0 8px 22px rgba(50, 53, 93, 0.05);
  padding: 0.85rem;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  opacity: 0;
  transform: translateY(4px);
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.embedded-onboarding-host--ready {
  opacity: 1;
  transform: translateY(0);
}

.embedded-onboarding-host > * {
  display: block;
  width: 100%;
  min-height: 100%;
  border-radius: 10px;
  overflow: hidden;
}

.embedded-skeleton {
  border-radius: 0.8rem;
  padding: 0.95rem;
  margin-bottom: 0.55rem;
  border: none;
  background: linear-gradient(180deg, #ffffff, #fafbff);
}

.embedded-skeleton__line,
.embedded-skeleton__card {
  position: relative;
  overflow: hidden;
  background: #eceff3;
  border-radius: 0.5rem;
}

.embedded-skeleton__line::after,
.embedded-skeleton__card::after {
  content: '';
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.65), transparent);
  animation: embedded-shimmer 1.3s infinite;
}

.embedded-skeleton__line {
  height: 12px;
  margin-bottom: 0.55rem;
}

.embedded-skeleton__line--title {
  width: 50%;
  height: 16px;
}

.embedded-skeleton__line--short {
  width: 65%;
}

.embedded-skeleton__card {
  height: 180px;
  margin-top: 0.8rem;
}

.embedded-skeleton__card--small {
  height: 58px;
  margin-top: 0.7rem;
}

@keyframes embedded-shimmer {
  100% {
    transform: translateX(100%);
  }
}

.step-content-wrapper {
  flex-grow: 1;
  padding: 0 !important;
  max-width: 640px;
  margin: 0 auto;
  width: 100%;
  min-width: 0;
  overflow: visible;
  box-sizing: border-box;
}

.step-content-wrapper > .container-fluid {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  padding-left: 0.75rem;
  padding-right: 0.75rem;
  overflow-x: clip;
  box-sizing: border-box;
}

.app-footer {
  position: sticky;
  bottom: 0;
  width: 100%;
  z-index: 100;
  background: transparent;
  pointer-events: none;
}

.app-footer__inner {
  background: #fff;
  border-top: 1px solid #f0f0f0;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.07);
  padding: 0.75rem 1.25rem 1rem;
  pointer-events: all;
  max-width: 640px;
  margin: 0 auto;
  border-radius: 16px 16px 0 0;
}

.app-footer__step-hint {
  font-size: 0.72rem;
  color: #b0b0b0;
  text-align: center;
  margin-bottom: 0.6rem;
}

.app-footer__actions {
  display: grid;
  grid-template-columns: auto minmax(320px, 1fr) auto;
  align-items: center;
  gap: 10px;
}

.btn-footer-back {
  background: #fff;
  border: 1.5px solid #e0e0e0;
  color: #6c757d;
  border-radius: 0.75rem;
  padding: 0.65rem 1.25rem;
  font-size: 0.88rem;
  font-weight: 600;
  transition: all 0.18s;
  flex-shrink: 0;
}

.btn-footer-back:hover:not(:disabled) {
  border-color: #adb5bd;
  color: #32355d;
}

.btn-footer-finish {
  grid-column: 2;
  justify-self: center;
  width: 100%;
  max-width: 520px;
  background: #ff50a3 !important;
  background-image: linear-gradient(135deg, #ff50a3, #d63384) !important;
  color: #fff !important;
  border: none;
  border-radius: 0.75rem;
  padding: 0.65rem 1.25rem;
  font-size: 0.92rem;
  font-weight: 700;
  box-shadow: 0 4px 14px rgba(255, 80, 163, 0.35);
  transition: opacity 0.18s, transform 0.15s;
}

.btn-footer-finish:hover:not(:disabled) {
  background: #e8417f !important;
  background-image: linear-gradient(135deg, #e8417f, #c0296e) !important;
  color: #fff !important;
  transform: translateY(-1px);
}

.btn-footer-finish:focus,
.btn-footer-finish:active,
.btn-footer-finish:focus-visible {
  background: #e8417f !important;
  background-image: linear-gradient(135deg, #e8417f, #c0286e) !important;
  color: #fff !important;
  outline: none;
  box-shadow: 0 0 0 0.2rem rgba(255, 80, 163, 0.35);
}

.btn-footer-finish:disabled,
.btn-footer-back:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.btn-color-site {
  background-color: #ff50a3;
  color: #fff;
  border: 1px solid #ff50a3;
  transition: background-color 0.2s;
}

.btn-color-site:hover {
  background-color: #e6408f;
  border-color: #e6408f;
  color: #fff;
}

.tab-nav {
  display: flex;
  gap: 0.5rem;
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 0;
  margin-bottom: 1rem;
}

.tab-btn {
  background: none;
  border: none;
  border-bottom: 2.5px solid transparent;
  padding: 0.5rem 1rem;
  font-size: 0.88rem;
  font-weight: 600;
  color: #6c757d;
  cursor: pointer;
  margin-bottom: -2px;
  transition: color 0.18s, border-color 0.18s;
  border-radius: 0;
}

.tab-btn.active {
  color: #ff50a3;
  border-bottom-color: #ff50a3;
}

.tab-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 767.98px) {
  .create-event-page {
    min-width: 0;
  }

  .step-content-wrapper {
    padding: 1.5rem 1rem 6.5rem !important;
  }

  .step-content {
    padding-bottom: 0 !important;
  }

  .payment-stage-header__top {
    align-items: center;
    flex-direction: row;
    gap: 0.5rem;
  }

  .payment-stage-benefits {
    display: none;
  }

  .app-footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    z-index: 1031;
  }

  .app-footer__inner {
    max-width: 100%;
    border-radius: 12px 12px 0 0;
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }

  .app-footer__actions {
    display: flex;
    gap: 10px;
  }

  .btn-footer-back,
  .btn-footer-finish {
    padding: 0.6rem 0.9rem;
    font-size: 0.86rem;
  }
}

</style>

<style>

@media (max-width: 767.98px) {
  .main-content-area main {
    padding-top: 0rem !important;
  }
}

</style>
